import { useState } from 'react'
import { TPatient } from 'src/types/patient'
import {
  CurecallBanner,
  CurecallConfirmationModal,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { PatientService } from 'src/services/patient.service'
import { useTranslation } from 'react-i18next'
import { useNotification } from 'src/providers/notification.provider'
import { usePatientList } from 'src/pages/medical/patients/_components/patients.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useQueryClient } from '@tanstack/react-query'

export interface Props {
  patient: TPatient
}

const ArchivedBanner = ({ patient }: Props) => {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const { notify } = useNotification()
  const { updatePatientList } = usePatientList()
  const queryClient = useQueryClient()

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false)

  const handleUnArchivePatient = async () => {
    try {
      const updatedPatient = await PatientService.update(project.id, patient.id, {
        isArchived: false,
      })
      updatePatientList({ method: 'PUT', data: { patient: updatedPatient } })
      queryClient.setQueryData(['patients', project.id, patient.id], (oldData: any) => {
        return oldData ? { ...oldData, ...updatedPatient } : updatedPatient
      })
      notify({
        title: t('common.actions.success'),
        message: t('components.patient.archive-or-delete.unarchive-notification-success'),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
    } catch {
      notify({
        title: t('common.actions.error'),
        message: t(`components.patient.archive-or-delete.unarchive-notification-error`),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    } finally {
      setIsConfirmationModalOpen(false)
    }
  }

  return (
    <div className="c-mt-6">
      <CurecallBanner
        appearance="WARNING"
        actionLabel={t('components.patient.archive-or-delete.click-here-to-unarchive')}
        action={() => setIsConfirmationModalOpen(true)}
      >
        {t('components.patient.archive-or-delete.patient-archived-you-cannot-see-followups')}
      </CurecallBanner>
      <CurecallConfirmationModal
        isOpen={isConfirmationModalOpen}
        color="ALERT"
        onConfirm={handleUnArchivePatient}
        onCancel={() => setIsConfirmationModalOpen(false)}
        title={t('components.patient.archive-or-delete.confirmation-modal-unarchive-title')}
        cancelButtonLabel={t('common.actions.cancel')}
        confirmButtonLabel={t('components.patient.archive-or-delete.unarchive')}
      >
        <div className="c-flex c-flex-col c-gap-4 c-items-start">
          <span>{t('components.patient.archive-or-delete.confirmation-modal-unarchive-description')}</span>
          <span className="c-mx-auto c-font-semibold c-text-lg">
            {patient.firstname} {patient.lastname}
          </span>
        </div>
      </CurecallConfirmationModal>
    </div>
  )
}

export default ArchivedBanner
